/* Adding to a contract that is already open.
 *
 * The mirror of `closePortion`, and the half that was missing. Taking part of a
 * position off could be recorded; putting more on could not — the only thing
 * the app offered was "add a contract", which writes a second row for what the
 * exchange shows as one position, each row carrying its own entry price and
 * neither carrying the real one. Two wrong averages instead of one right one.
 *
 * What the exchange knows and this cannot work out is asked for: the price the
 * addition filled at, and the liquidation price the exchange recomputed
 * afterwards. What is arithmetic is computed here and never copied off a
 * screen — the same division of labour that keeps `sizeUsdt` off the MEXC card.
 *
 * The blended entry is weighted by contracts, not by money:
 *
 *     contracts = notional / price          for each part
 *     entry'    = (notional + added) / (contracts + contractsAdded)
 *
 * which is the average price actually paid per contract. Averaging the two
 * prices directly would be right only when both parts happen to be the same
 * size, and wrong by more the further apart they are.
 *
 * Leverage is not a parameter. MEXC sets it per position rather than per fill,
 * so a position whose leverage changed did not have something added to it — it
 * was altered, and that is a correction, which is a different act with a
 * different form. */

import type { Perp } from '../types';

export interface Addition {
  /** Margin posted for the added part, in USDT. */
  marginUsdt: number;
  /** What it filled at. */
  price: number;
  /** The liquidation price the exchange shows *after* the addition. Adding
      margin at a price away from your entry moves this figure, and by how much
      is the exchange's own maintenance arithmetic — computing it here would be
      inventing the number that decides when you get liquidated. */
  liqPrice: number;
}

export interface AddOutcome {
  next: Perp;
  /** Notional the addition put on: margin times the contract's leverage. */
  addedNotional: number;
  addedContracts: number;
  /** The entry price before, so a screen can show what moved. */
  previousEntry: number;
}

/** Null when the addition does not describe anything that can be added: a
    closed contract, or a figure at or below zero. The caller says so rather
    than saving a position built out of a bad number. */
export function addToPosition(perp: Perp, add: Addition): AddOutcome | null {
  if (perp.closedAt) return null;
  if (!(add.marginUsdt > 0) || !(add.price > 0) || !(add.liqPrice > 0)) return null;
  if (!(perp.entryPrice > 0) || !(perp.leverage > 0)) return null;

  const addedNotional = add.marginUsdt * perp.leverage;
  const addedContracts = addedNotional / add.price;
  const heldContracts = perp.sizeUsdt / perp.entryPrice;

  const totalNotional = perp.sizeUsdt + addedNotional;
  const totalContracts = heldContracts + addedContracts;
  if (!(totalContracts > 0)) return null;

  return {
    next: {
      ...perp,
      sizeUsdt: totalNotional,
      marginUsdt: perp.marginUsdt + add.marginUsdt,
      entryPrice: totalNotional / totalContracts,
      liqPrice: add.liqPrice,
    },
    addedNotional,
    addedContracts,
    previousEntry: perp.entryPrice,
  };
}

/* ---------- correcting what was written down ---------- */

export interface Correction {
  /** The contract this position is actually on. Changing it is the point: a
      position opened on the wrong pair — the USDC book rather than the USDT
      one, or a lookalike ticker — has every figure right and the marks going to
      the wrong instrument, which shows up as a price that never quite matches
      the exchange card. Absent leaves it alone. */
  symbol?: string;
  display?: string;
  side: Perp['side'];
  leverage: number;
  marginUsdt: number;
  entryPrice: number;
  liqPrice: number;
  takeProfit?: number | undefined;
}

/** A contract as it should have been recorded.
 *
 * Not a close and not an addition: nothing was bought or sold, the book simply
 * had the wrong figures. So no result is realised and `realizedUsdt` is left
 * exactly where it was — a typo in an entry price must not turn into a gain.
 *
 * `sizeUsdt` is derived rather than asked for, because it is margin times
 * leverage by definition and that invariant is what makes the §5.1 unrealised
 * formula exact. It holds through a partial close, which scales margin and
 * notional together, and through an addition, which adds margin times the same
 * leverage — so it is safe to restate here. */
export function correctPosition(perp: Perp, fix: Correction): Perp | null {
  if (!(fix.leverage > 0) || !(fix.marginUsdt > 0)) return null;
  if (!(fix.entryPrice > 0) || !(fix.liqPrice > 0)) return null;

  const next: Perp = {
    ...perp,
    ...(fix.symbol?.trim() ? { symbol: fix.symbol.trim() } : {}),
    ...(fix.display?.trim() ? { display: fix.display.trim() } : {}),
    side: fix.side,
    leverage: fix.leverage,
    marginUsdt: fix.marginUsdt,
    sizeUsdt: fix.marginUsdt * fix.leverage,
    entryPrice: fix.entryPrice,
    liqPrice: fix.liqPrice,
  };

  if (fix.takeProfit !== undefined && fix.takeProfit > 0) next.takeProfit = fix.takeProfit;
  else delete next.takeProfit;

  return next;
}
